import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalService } from '../../../core/services/modal.service';
import { InvoiceService } from '../../../core/services/invoice.service';
import { LoadService } from '../../../core/services/load.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { Invoice } from '../../../core/models/invoice.model';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { InputComponent } from '../../../shared/ui/forms/input/input.component';
import { FormFieldComponent } from '../../../shared/ui/forms/form-field/form-field.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';

@Component({
  selector: 'app-resend-invoice-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    InputComponent,
    FormFieldComponent,
    TextComponent,
    LucideIconComponent,
  ],
  templateUrl: './resend-invoice-modal.component.html',
})
export class ResendInvoiceModalComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly loadService = inject(LoadService);
  private readonly toastService = inject(ToastService);

  readonly invoice = (this.modalService.state() as any)?.data?.invoice as Invoice;
  readonly onSuccess = (this.modalService.state() as any)?.data?.onSuccess as (() => void) | undefined;

  recipientEmails = signal<string>('');
  recipientName = signal<string>('');
  isSending = signal<boolean>(false);

  ngOnInit(): void {
    if (this.invoice) {
      this.recipientEmails.set(this.invoice.billedToEmail || '');
      this.recipientName.set(this.invoice.billedToName || '');
    }
  }

  close(): void {
    this.modalService.close();
  }

  async send(): Promise<void> {
    const rawEmails = this.recipientEmails().trim();
    if (!rawEmails) {
      this.toastService.show('error', 'Please provide at least one recipient email address.');
      return;
    }

    this.isSending.set(true);
    try {
      // 1. Update invoice document if emails or name changed
      await this.invoiceService.updateInvoiceRecipients(
        this.invoice.id,
        rawEmails,
        this.recipientName()
      );

      // 2. Trigger the cloud function to send invoice package
      const res = await this.loadService.emailInvoiceDocuments(
        this.invoice.invoiceNumber,
        rawEmails
      );

      // 3. Mark invoice as sent if it was still in processing/draft
      if (this.invoice.status === 'draft' || this.invoice.status === 'processing') {
        try {
          await this.invoiceService.markInvoiceSent(this.invoice.id);
        } catch (e) {
          console.warn('Failed to advance invoice status to sent:', e);
        }
      }

      const sentTo = res?.sentTo || rawEmails;
      this.toastService.show('success', `Invoice package resent to ${sentTo}`);
      this.onSuccess?.();
      this.close();
    } catch (err: any) {
      console.error('Failed to resend invoice:', err);
      this.toastService.show('error', err.message || 'Failed to resend invoice');
    } finally {
      this.isSending.set(false);
    }
  }
}
